/**
 * Custom hooks wrapping TanStack Query with the platform's API client.
 *
 * These hooks integrate React Query with the existing `apiRequest` utility,
 * providing type-safe data fetching with automatic caching, refetching,
 * and error handling.
 */

import {
  useQuery,
  useMutation,
  useQueryClient,
  UseQueryOptions,
  UseMutationOptions,
  QueryKey,
} from '@tanstack/react-query';
import { apiRequest, ApiRequestOptions, ApiResult, ApiError } from '../services/api';

/**
 * Unwrap an API response, throwing on error so React Query treats it as a failure.
 */
async function unwrapApiResponse<T>(
  endpoint: string,
  options?: ApiRequestOptions,
): Promise<T> {
  const result = await apiRequest<T>(endpoint, options);

  if (!result.success) {
    const apiError = result as ApiError;
    const error = new Error(apiError.error.message) as Error & {
      code: string;
      details?: Record<string, unknown>;
    };
    error.code = apiError.error.code;
    error.details = apiError.error.details;
    throw error;
  }

  return (result as ApiResult<T>).data;
}

/**
 * Hook for GET requests with automatic caching and refetching.
 *
 * @example
 * ```tsx
 * const { data, isLoading, error } = useApiQuery<Server[]>(
 *   ['servers', companyId],
 *   `/companies/${companyId}/servers`
 * );
 * ```
 */
export function useApiQuery<T>(
  queryKey: QueryKey,
  endpoint: string,
  options?: Omit<UseQueryOptions<T, Error>, 'queryKey' | 'queryFn'>,
) {
  return useQuery<T, Error>({
    queryKey,
    queryFn: () => unwrapApiResponse<T>(endpoint),
    ...options,
  });
}

/**
 * Hook for mutations (POST, PUT, DELETE) with cache invalidation support.
 *
 * @example
 * ```tsx
 * const createServer = useApiMutation<Server, CreateServerPayload>(
 *   `/companies/${companyId}/servers`,
 *   { method: 'POST' },
 *   { onSuccess: () => queryClient.invalidateQueries({ queryKey: ['servers'] }) }
 * );
 *
 * createServer.mutate({ ip: '10.0.0.1', port: 22, ... });
 * ```
 */
export function useApiMutation<TData, TVariables = void>(
  endpoint: string | ((variables: TVariables) => string),
  requestOptions?: Omit<ApiRequestOptions, 'body'>,
  mutationOptions?: Omit<
    UseMutationOptions<TData, Error, TVariables>,
    'mutationFn'
  >,
) {
  return useMutation<TData, Error, TVariables>({
    mutationFn: async (variables: TVariables) => {
      const resolvedEndpoint =
        typeof endpoint === 'function' ? endpoint(variables) : endpoint;
      return unwrapApiResponse<TData>(resolvedEndpoint, {
        ...requestOptions,
        body: variables,
      });
    },
    ...mutationOptions,
  });
}

/**
 * Hook to get the query client for manual cache operations.
 * Re-exported for convenience.
 */
export { useQueryClient };
